import React, { useState, useRef } from 'react';
import { ChevronRight, Check } from 'lucide-react';
import { Permission } from '../types';

interface PermissionTreeProps {
  allPermissions: Permission[];
  selectedIds: number[];
  onChange?: (ids: number[]) => void;
  readOnly?: boolean;
}

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <ChevronRight
    size={14}
    strokeWidth={2.5}
    style={{
      transition: 'transform 0.2s',
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      flexShrink: 0,
    }}
  />
);

const CheckIcon = () => <Check size={13} strokeWidth={2.5} />;

// Mirrors the sidebar navigation order in Layout.tsx, so the role
// permission tree reads top-to-bottom the same way the menu does.
// Modules not in this list (e.g. unused ones) fall back to alphabetical order at the end.
const MODULE_ORDER = [
  'SalesInvoices',
  'Products',
  'Till',
  'Customers',
  'Menu',
  'Outlets',
  'Orders',
  'Modifiers',
  'Suppliers',
  'PurchaseOrders',
  'GRN',
  'Stock',
  'IssueNotes',
  'DamageNotes',
  'Reports',
  'Settings',
  'Warehouses',
  'Departments',
  'Taxes',
  'PaymentTypes',
  'Roles',
  'Users',
  'Packages',
];

const PermissionTree: React.FC<PermissionTreeProps> = ({
  allPermissions,
  selectedIds,
  onChange,
  readOnly = false,
}) => {
  const grouped = allPermissions.reduce<Record<string, Permission[]>>((acc, p) => {
    (acc[p.module] = acc[p.module] ?? []).push(p);
    return acc;
  }, {});

  const modules = Object.keys(grouped).sort((a, b) => {
    const ia = MODULE_ORDER.indexOf(a);
    const ib = MODULE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(modules.map((m) => [m, true]))
  );

  const toggleModule = (module: string) =>
    setExpanded((prev) => ({ ...prev, [module]: !prev[module] }));

  const togglePermission = (id: number) => {
    if (!onChange) return;
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id]
    );
  };

  const toggleModulePermissions = (perms: Permission[]) => {
    if (!onChange) return;
    const ids = perms.map((p) => p.id);
    const allSelected = ids.every((id) => selectedIds.includes(id));
    onChange(
      allSelected
        ? selectedIds.filter((id) => !ids.includes(id))
        : [...new Set([...selectedIds, ...ids])]
    );
  };

  const expandAll = () => setExpanded(Object.fromEntries(modules.map((m) => [m, true])));
  const collapseAll = () => setExpanded(Object.fromEntries(modules.map((m) => [m, false])));

  return (
    <div className="permission-tree">
      {!readOnly && (
        <div className="permission-tree__toolbar">
          <button type="button" className="permission-tree__tool-btn" onClick={expandAll}>
            Expand All
          </button>
          <button type="button" className="permission-tree__tool-btn" onClick={collapseAll}>
            Collapse All
          </button>
          {onChange && (
            <>
              <button
                type="button"
                className="permission-tree__tool-btn permission-tree__tool-btn--primary"
                onClick={() => onChange(allPermissions.map((p) => p.id))}
              >
                Select All
              </button>
              <button
                type="button"
                className="permission-tree__tool-btn"
                onClick={() => onChange([])}
              >
                Clear All
              </button>
            </>
          )}
        </div>
      )}

      {modules.map((module) => {
        const perms = grouped[module];
        const isExpanded = expanded[module];
        const selectedInModule = perms.filter((p) => selectedIds.includes(p.id));
        const allModuleSelected = selectedInModule.length === perms.length;
        const someModuleSelected = selectedInModule.length > 0 && !allModuleSelected;

        if (readOnly && selectedInModule.length === 0) return null;

        return (
          <div key={module} className="permission-tree__module">
            <div className="permission-tree__module-header" onClick={() => toggleModule(module)}>
              <div className="permission-tree__module-left">
                {!readOnly && onChange && (
                  <ModuleCheckbox
                    allSelected={allModuleSelected}
                    someSelected={someModuleSelected}
                    onClick={(e) => { e.stopPropagation(); toggleModulePermissions(perms); }}
                  />
                )}
                <ChevronIcon expanded={isExpanded} />
                <span className="permission-tree__module-name">{module}</span>
              </div>
              <span className="permission-tree__module-count">
                {selectedInModule.length}/{perms.length}
              </span>
            </div>

            {isExpanded && (
              <div className="permission-tree__items">
                {perms.map((p) => {
                  const isSelected = selectedIds.includes(p.id);
                  return (
                    <div
                      key={p.id}
                      className={`permission-tree__item ${isSelected ? 'permission-tree__item--selected' : ''}`}
                      onClick={readOnly ? undefined : () => togglePermission(p.id)}
                      style={{ cursor: readOnly ? 'default' : 'pointer' }}
                    >
                      {readOnly ? (
                        <span className={`permission-tree__check-icon ${isSelected ? 'permission-tree__check-icon--yes' : 'permission-tree__check-icon--no'}`}>
                          {isSelected ? <CheckIcon /> : null}
                        </span>
                      ) : (
                        <input
                          type="checkbox"
                          className="permission-tree__checkbox"
                          checked={isSelected}
                          onChange={() => togglePermission(p.id)}
                          onClick={(e) => e.stopPropagation()}
                          readOnly={readOnly}
                        />
                      )}
                      <div className="permission-tree__item-info">
                        <span className="permission-tree__item-name">{p.name}</span>
                        {p.description && (
                          <span className="permission-tree__item-desc">{p.description}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {readOnly && modules.every((m) => grouped[m].every((p) => !selectedIds.includes(p.id))) && (
        <p className="permission-tree__empty">No permissions assigned to this role.</p>
      )}
    </div>
  );
};

const ModuleCheckbox: React.FC<{
  allSelected: boolean;
  someSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
}> = ({ allSelected, someSelected, onClick }) => {
  const ref = useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = someSelected;
  }, [someSelected]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="permission-tree__checkbox permission-tree__checkbox--module"
      checked={allSelected}
      onChange={() => {}}
      onClick={onClick}
    />
  );
};

export default PermissionTree;
